export interface StackFrameInfo {
  functionName?: string;
  fileName?: string;
  line?: number;
  column?: number;
}

// Requires the "//" after the scheme so a Windows drive letter ("D:\...") is
// never mistaken for a URL scheme — the WHATWG URL parser accepts "d:..." as
// a valid (wrong) URL and silently drops the drive letter.
const URL_SCHEME_PATTERN = /^[A-Za-z][\d+.A-Za-z-]*:\/\//;

/** Keeps the full path (not just the basename) — strips only the cache-busting query string. */
function extractFilePath(filePath: string): string {
  const withoutQuery = filePath.split('?')[0] ?? filePath;
  if (!URL_SCHEME_PATTERN.test(withoutQuery)) {
    return withoutQuery;
  }

  try {
    return new URL(withoutQuery).pathname || withoutQuery;
  } catch {
    return withoutQuery;
  }
}

/**
 * Parses the first meaningful frame of a V8-style stack trace — works for both a
 * regular `Error.stack` and React's `componentStack` (same "at X (file:line:col)"
 * shape). Falls back gracefully as less information is available in the frame.
 */
export function parseTopStackFrame(
  stack: string | null | undefined
): StackFrameInfo | undefined {
  if (!stack) {
    return undefined;
  }

  const frameLine = stack
    .split('\n')
    .map((line) => line.trim())
    .find((line) => /^(?:at|in)\s+/.test(line));

  if (!frameLine) {
    return undefined;
  }

  const withLocation =
    /^(?:at|in)\s+(?:async\s+)?([^\s(]+)\s*\(([^)]+):(\d+):(\d+)\)$/.exec(
      frameLine
    );
  if (withLocation) {
    const [, functionName, filePath, line, column] = withLocation;
    return {
      functionName,
      fileName: extractFilePath(filePath),
      line: Number.parseInt(line, 10),
      column: Number.parseInt(column, 10),
    };
  }

  const anonymousLocation = /:(\d+):(\d+)$/.exec(frameLine);
  const anonymousPrefix = /^(?:at|in)\s+(?:async\s+)?/.exec(frameLine);
  if (anonymousPrefix && anonymousLocation?.index != null) {
    const filePath = frameLine.slice(
      anonymousPrefix[0].length,
      anonymousLocation.index
    );
    if (filePath.length > 0) {
      return {
        fileName: extractFilePath(filePath),
        line: Number.parseInt(anonymousLocation[1], 10),
        column: Number.parseInt(anonymousLocation[2], 10),
      };
    }
  }

  const nameOnly = /^(?:at|in)\s+([\w$.]+)/.exec(frameLine);
  return nameOnly ? { functionName: nameOnly[1] } : undefined;
}

// React writes these built-in wrappers into a componentStack too, but they never
// name one of this app's own components.
const BUILT_IN_FRAME_NAMES = new Set([
  'Activity',
  'Fragment',
  'Lazy',
  'Profiler',
  'StrictMode',
  'Suspense',
  'SuspenseList',
  'ViewTransition',
]);

/**
 * Every component name in a React `componentStack`, innermost (the one that threw)
 * first. Host elements ("div", "span") and React's own built-in wrappers are
 * skipped, so what's left is the chain of real components.
 */
export function listComponentStackNames(
  componentStack: string | null | undefined
): Array<string> {
  if (!componentStack) {
    return [];
  }

  const names: Array<string> = [];
  for (const line of componentStack.split('\n')) {
    const frame = /^\s*(?:at|in)\s+([\w$.]+)(?:\s+\(([^)]*)\))?/.exec(line);
    const name = frame?.[1];
    if (!name || BUILT_IN_FRAME_NAMES.has(name)) {
      continue;
    }
    const location = frame[2];
    const isHostElement = /^[a-z]/.test(name) && (location === undefined || location === '<anonymous>');
    if (!isHostElement) {
      names.push(name);
    }
  }
  return names;
}

/**
 * The page component the router rendered for the current route — the component
 * directly inside the innermost router frame in the componentStack. Undefined when
 * no router frame is found (e.g. the crash happened outside the routed area, or the
 * build minified the router's own function names).
 */
export function findRoutePageComponentName(
  componentStack: string | null | undefined,
  routerFrameNames: ReadonlySet<string>
): string | undefined {
  const names = listComponentStackNames(componentStack);
  const routerFrameIndex = names.findIndex((name) => routerFrameNames.has(name));
  return routerFrameIndex > 0 ? names[routerFrameIndex - 1] : undefined;
}
